import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Collapse,
  DialogContentText,
  Tooltip,
} from "@mui/material";
import {
  EditRounded,
  DeleteRounded,
  ExpandMoreRounded,
  InfoOutlined,
  LocalFireDepartmentRounded,
  NotificationsRounded,
  NotificationsOffRounded,
  WarningAmberRounded,
  WarningRounded,
} from "@mui/icons-material";
import { format, isValid, parseISO } from "date-fns";
import { Trans, useTranslation } from "react-i18next";
import { dateLocale } from "i18n";
import ConfirmDialog from "components/common/ConfirmDialog";
import { doseWarningText } from "data/medicationDoseLimits";
import { useDoseLimit } from "hooks/useDoseLimits";
import { useDrugLabel, useDrugReference } from "hooks/useDrugReference";
import { isCurrentMedication } from "utils/drugInteractions";
import MedicationAlerts from "./MedicationAlerts";
import MedicationLabelInfo from "./MedicationLabelInfo";
import MedicationInfoDialog from "./MedicationInfoDialog";
import MedicationHabitTracker from "../medicationprogress/MedicationHabitTracker";
// import { adherenceAnalyticsService } from '../../services/adherenceAnalyticsService';
// import AnalogTimePicker from './AnalogTimePicker';
// import * as Sentry from '@sentry/browser';
// import { getCachedUserTimezone } from '../../utils/timezoneUtils';

/** "yyyy-MM-dd" → "Apr 2, 2026"; anything unparsable shown as stored. */
const formatRecordDate = (value, t) => {
  const parsed = parseISO(String(value));
  return isValid(parsed)
    ? format(parsed, t("common.dateFormat"), { locale: dateLocale() })
    : String(value);
};

/** "yyyy" shown as is; "yyyy-MM" → "April 2026". */
const formatApproxDate = (value, t) =>
  value.length === 4
    ? value
    : format(parseISO(`${value}-01`), t("common.monthYearFormat"), { locale: dateLocale() });

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme }) => ({
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: ({ expand }) => !expand,
      style: {
        transform: "rotate(0deg)",
      },
    },
    {
      props: ({ expand }) => !!expand,
      style: {
        transform: "rotate(180deg)",
      },
    },
  ],
}));

// Reusable badge components
const MedicationBadges = ({
  medication,
  optimisticReminderStatus,
  streakData,
  doseWarning,
  sx = {},
}) => {
  const { t } = useTranslation("medications");
  return (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ...sx }}>
    {/* Verified Badge */}
    {medication.rxcui && (
      <Chip
        label={t("card.verified")}
        size="small"
        color="success"
        variant="outlined"
        sx={{ fontSize: "0.7rem" }}
      />
    )}

    {/* Finished: a past end date, or reported stopped (e.g. to the assistant). Shown up here
        because a stopped medication otherwise looks exactly like a current one. */}
    {!isCurrentMedication(medication) && (
      <Chip label={t("card.stopped")} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
    )}

    {/* Saved dose more than double the label ceiling — details in the expanded card */}
    {doseWarning && (
      <Tooltip title={doseWarning}>
        <Chip
          icon={<WarningAmberRounded />}
          label={t("card.checkDose")}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ fontSize: "0.7rem" }}
        />
      </Tooltip>
    )}

    {/* TODO: Notification Status Chip disabled until Twilio BAA is in place */}
    {/* <Chip
      icon={
        optimisticReminderStatus?.enabled ? (
          <NotificationsRounded sx={{ fontSize: 14 }} />
        ) : (
          <NotificationsOffRounded sx={{ fontSize: 14 }} />
        )
      }
      label={optimisticReminderStatus?.enabled ? "Alerts On" : "Alerts Off"}
      size="small"
      color={optimisticReminderStatus?.enabled ? "primary" : "default"}
      variant="outlined"
      sx={{
        fontSize: "0.7rem",
        height: 24,
        "& .MuiChip-icon": {
          fontSize: 14,
        },
      }}
    /> */}

    {/* Streak Badge */}
    {streakData?.currentStreak > 0 && (
      <Chip
        icon={<LocalFireDepartmentRounded />}
        label={streakData.currentStreak}
        size="small"
        color="warning"
        variant="filled"
        sx={{
          fontSize: "0.75rem",
          height: 24,
        }}
      />
    )}
  </Box>
  );
};

const MedicationCard = ({
  medication,
  reminderStatus,
  trackingSummary,
  onEdit,
  onDelete,
  onAdherenceChange,
  canEnableMoreAlerts = true,
}) => {
  const { t } = useTranslation("medications");
  const [loading, setLoading] = useState(false); // For switch toggle operations
  const [switchLoading, setSwitchLoading] = useState(false); // Track switch operations
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  // Optimistic reminder status - starts with prop value, updates immediately on toggle
  const [optimisticReminderStatus, setOptimisticReminderStatus] =
    useState(reminderStatus);

  // Update optimistic status when real prop changes
  React.useEffect(() => {
    if (reminderStatus) {
      setOptimisticReminderStatus(reminderStatus);
    }
  }, [reminderStatus]);

  // Extract tracking data from prop
  const adherenceData = trackingSummary?.adherenceData || null;
  const streakData = trackingSummary?.streakData || null;
  const badges = trackingSummary?.badges || [];

  // The form's dose check, re-run on the saved entry: catches doses saved before the check
  // existed, edited elsewhere, or imported from a document. One cached read per medication.
  const doseLimit = useDoseLimit({ rxcui: medication.rxcui, name: medication.name });
  const doseWarning = doseWarningText(doseLimit, medication.dosage);

  // Only to know whether there is a boxed warning at all. Both documents are the ones the
  // expanded card reads, and drugReferenceService caches each by id for the session, so an
  // open card and a second card on the same drug cost nothing more.
  const { reference } = useDrugReference(medication.rxcui, medication.name);
  const { label } = useDrugLabel(reference?.labelSetId);
  const hasBoxedWarning = !!label?.sections?.boxedWarning;

  // Toggle expanded state
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Get 7-day data from Auth context - simple and clean
  const get7DayData = () => {
    // We'll add this prop to get tracking data from parent
    return trackingSummary?.last7Days || [];
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await onDelete(medication);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting medication:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Card>
        {/* Name over dose, with the controls on the right: the same card as the
            MasterChart sections. The rest (prescriber, alerts, tracking) is
            behind the expand arrow. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: 2,
            pr: 1,
            py: 1.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {medication.name}
            </Typography>
            {(medication.dosage || medication.frequency) && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {[medication.dosage, medication.frequency]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            )}
            {/* On small screens the badges sit under the dose: "Stopped" and "Check dose"
                are the only sign of either until the card is opened. */}
            <MedicationBadges
              medication={medication}
              optimisticReminderStatus={optimisticReminderStatus}
              streakData={streakData}
              doseWarning={doseWarning}
              sx={{ display: { xs: "flex", sm: "none" }, flexWrap: "wrap", mt: 0.5 }}
            />
          </Box>

          <MedicationBadges
            medication={medication}
            optimisticReminderStatus={optimisticReminderStatus}
            streakData={streakData}
            doseWarning={doseWarning}
            sx={{ display: { xs: "none", sm: "flex" }, flexShrink: 0 }}
          />

          <Box sx={{ display: "flex", flexShrink: 0 }}>
            <IconButton
              onClick={() => onEdit(medication)}
              aria-label={t("card.editAria")}
              disabled={deleteLoading}
            >
              <EditRounded />
            </IconButton>
            <IconButton
              onClick={() => setDeleteDialogOpen(true)}
              aria-label={t("card.deleteAria")}
              disabled={deleteLoading}
            >
              <DeleteRounded />
            </IconButton>
            <Tooltip title={t("card.drugInfo")}>
              <IconButton onClick={() => setInfoOpen(true)} aria-label={t("card.drugInfoAria")}>
                <InfoOutlined />
              </IconButton>
            </Tooltip>
            {/* Next to the "i" because it answers the same question — what is this drug — and
                opens the same dialog, where the warning's own text is. The icon carries the
                colour on its own: a filled chip up in the title row made every boxed-warning
                drug look like an emergency, and most of a patient's list can have one. */}
            {hasBoxedWarning && (
              <Tooltip title={t("card.boxedWarningTooltip")}>
                <IconButton
                  onClick={() => setInfoOpen(true)}
                  aria-label={t("card.boxedWarningAria")}
                  color="warning"
                >
                  <WarningRounded />
                </IconButton>
              </Tooltip>
            )}
            <ExpandMore
              expand={expanded}
              onClick={toggleExpanded}
              aria-expanded={expanded}
              aria-label={t("card.showMore")}
            >
              <ExpandMoreRounded />
            </ExpandMore>
          </Box>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent sx={{ pt: 1 }}>
            {/* Medication Details Section. Dosage and frequency are on the row
                above, so this is only what the row has no room for. */}
            {(medication.prescribedBy ||
              medication.pharmacy ||
              medication.notes ||
              medication.startDate ||
              medication.startDateApprox ||
              medication.endDate ||
              medication.status === "stopped" ||
              medication.lastConfirmedAt) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t("card.details")}
                </Typography>

                {/* Start / end dates */}
                {medication.startDate && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>{t("card.started")}</strong> {formatRecordDate(medication.startDate, t)}
                  </Typography>
                )}

                {/* Only when there is no exact date: the assistant records a
                    month or a year when that is all the patient could say. */}
                {!medication.startDate && medication.startDateApprox && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>{t("card.startedAround")}</strong>{" "}
                    {formatApproxDate(medication.startDateApprox, t)}
                  </Typography>
                )}

                {medication.endDate && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>{t("card.until")}</strong> {formatRecordDate(medication.endDate, t)}
                  </Typography>
                )}

                {/* Reported stopped without a date, so endDate stays empty. */}
                {!medication.endDate && medication.status === "stopped" && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>{t("card.stopped")}</strong>
                    {/* A month or a year, when that is all the patient could say. */}
                    {medication.endDateApprox
                      ? t("card.stoppedAround", {
                          date: formatApproxDate(medication.endDateApprox, t),
                        })
                      : ""}
                    {medication.statusReportedAt
                      ? t("card.reportedOn", {
                          date: formatRecordDate(medication.statusReportedAt, t),
                        })
                      : ""}
                  </Typography>
                )}

                {/* Written when the patient confirms an entry to the assistant ("still taking
                    it", a dose, a frequency) — otherwise that answer changes nothing visible. */}
                {medication.lastConfirmedAt && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t("card.confirmedOn", { date: formatRecordDate(medication.lastConfirmedAt, t) })}
                  </Typography>
                )}

                {/* Prescriber & Pharmacy */}
                {medication.prescribedBy && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>{t("card.prescribedBy")}</strong> {medication.prescribedBy}
                  </Typography>
                )}

                {medication.pharmacy && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t("card.pharmacy")}</strong> {medication.pharmacy}
                  </Typography>
                )}

                {/* Notes */}
                {medication.notes && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{t("card.notes")}</strong> {medication.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <MedicationLabelInfo
              medication={medication}
              doseWarning={doseWarning}
              doseSource={doseLimit?.source}
            />

            {/* Alert Settings Section */}
            <MedicationAlerts
              medication={medication}
              reminderStatus={reminderStatus}
              optimisticReminderStatus={optimisticReminderStatus}
              setOptimisticReminderStatus={setOptimisticReminderStatus}
              onAdherenceChange={onAdherenceChange}
              loading={loading}
              setLoading={setLoading}
              switchLoading={switchLoading}
              setSwitchLoading={setSwitchLoading}
              trackingSummary={trackingSummary}
            />

            {/* Recent Tracking Section */}
            <MedicationHabitTracker
              medications={[medication]}
              calendarDataByMedication={
                new Map([
                  [medication.id, { dailyData: get7DayData(), isEmpty: false }],
                ])
              }
              loading={false}
              compact={true}
              singleMedicationId={medication.id}
              alertsEnabled={optimisticReminderStatus?.enabled}
            />

            {/* Simple Streak Summary */}
            {streakData && streakData.currentStreak > 0 && (
              <Box sx={{ mt: 1, textAlign: "left" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.75rem",
                    lineHeight: 1.2,
                    opacity: 0.8,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <LocalFireDepartmentRounded
                    sx={{ fontSize: "0.9rem", color: "warning.main" }}
                  />
                  {t("card.streak", { count: streakData.currentStreak })}
                  {streakData.bestStreak > 0 &&
                    streakData.bestStreak !== streakData.currentStreak && (
                      <span>
                        {" "}
                        {t("card.bestStreak", { count: streakData.bestStreak })}
                      </span>
                    )}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Collapse>
      </Card>

      {infoOpen && (
        <MedicationInfoDialog
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          medication={medication}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        title={t("card.deleteDialog.title")}
        message={
          <Box>
            <DialogContentText sx={{ mb: 2 }}>
              <Trans
                ns="medications"
                i18nKey="card.deleteDialog.confirm"
                values={{ name: medication.name }}
                components={{ 1: <strong /> }}
              />
            </DialogContentText>
            <DialogContentText>
              {t("card.deleteDialog.warning")}
            </DialogContentText>
          </Box>
        }
        confirmLabel={t("card.deleteDialog.title")}
        cancelLabel={t("common.cancel")}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        confirmColor="error"
      />
    </>
  );
};

export default MedicationCard;
